import { ChangeDetectionStrategy, Component, effect, inject, input, output, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { SharedModule } from '@/shared.module';
import { LanguageService, NnnService } from '@/services';
import type {
  NnnRegistrationResult,
  NnnRegistrationResultIndicator,
  NocScoreValue,
  NocZorgresultaatSummary,
} from '@/services';
import type { EditNnnResultDialogData, EditNnnResultDialogResult, SaveState } from '../../types/careplan-nnn.types';
import { reuseOrCreateByRef } from '../../utils/reuse-or-create-by-ref';
import { EditNnnResultDialogComponent } from '../edit-nnn-result-dialog/edit-nnn-result-dialog.component';
import { NnnNotesCellComponent } from '../nnn-notes-cell/nnn-notes-cell.component';
import { NnnResultsInfoDialogComponent } from '../nnn-results-info-dialog/nnn-results-info-dialog.component';

/**
 * Structured NOC block of a careplan-NNN row: the picked zorgresultaten, each
 * with a "current → desired" score chip in the header, a line per selected
 * indicator (with its current and desired scores inline when registered) and its
 * own notes textarea, plus edit / remove / add actions. Emits the updated `results` array; the
 * parent owns the row and persists it.
 */
@Component({
  selector: 'app-nnn-results-section',
  standalone: true,
  imports: [SharedModule, NnnNotesCellComponent],
  templateUrl: './nnn-results-section.component.html',
  styleUrl: './nnn-results-section.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NnnResultsSectionComponent {
  private readonly nnnService = inject(NnnService);
  private readonly languageService = inject(LanguageService);
  private readonly dialog = inject(MatDialog);

  readonly results = input.required<NnnRegistrationResult[]>();
  readonly diagnoseCode = input<number | null>(null);
  readonly nocIndex = input<Map<string, NocZorgresultaatSummary>>(new Map());
  readonly editable = input(false);
  readonly saveState = input<SaveState>('');

  readonly resultsChange = output<NnnRegistrationResult[]>();

  private readonly indicatorLabels = signal<ReadonlyMap<string, string>>(new Map());
  private readonly scoreValues = signal<ReadonlyMap<string, NocScoreValue>>(new Map());
  private readonly fetchedZorgresultaten = new Set<number>();
  private readonly fetchedSchaalIds = new Set<string>();

  constructor() {
    effect(() => {
      for (const result of this.results()) {
        const zorgresultaat = this.nocIndex().get(result.nnnResultDbRef);
        if (zorgresultaat && !this.fetchedZorgresultaten.has(zorgresultaat.code)) {
          this.fetchedZorgresultaten.add(zorgresultaat.code);
          void this.loadIndicatorLabels(zorgresultaat.code);
          void this.loadOutcomeScoreLabels(zorgresultaat.code);
        }
      }
    });
  }

  private async loadIndicatorLabels(zorgresultaatCode: number): Promise<void> {
    const { data } = await this.nnnService.getNocIndicatoren(zorgresultaatCode);
    if (data) {
      this.indicatorLabels.update(labels => {
        const next = new Map(labels);
        for (const indicator of data) {
          next.set(String(indicator.code), indicator.omschrijving);
        }
        return next;
      });
      const schaalIds = new Set(data.map(indicator => indicator.scoreSchaalId).filter((id): id is string => !!id));
      for (const schaalId of schaalIds) {
        void this.loadScoreLabels(schaalId);
      }
    }
  }

  private async loadOutcomeScoreLabels(zorgresultaatCode: number): Promise<void> {
    const { data } = await this.nnnService.getNocOutcomeDetail(zorgresultaatCode);
    if (data?.scoreSchaalId) {
      void this.loadScoreLabels(data.scoreSchaalId);
    }
  }

  private async loadScoreLabels(schaalId: string): Promise<void> {
    if (this.fetchedSchaalIds.has(schaalId)) {
      return;
    }
    this.fetchedSchaalIds.add(schaalId);
    const { data } = await this.nnnService.getNocScoreValues(schaalId);
    if (data) {
      this.cacheScoreValues(data);
    }
  }

  private cacheScoreValues(scoreValues: NocScoreValue[]): void {
    this.scoreValues.update(values => {
      const next = new Map(values);
      for (const score of scoreValues) {
        next.set(score.id, score);
      }
      return next;
    });
  }

  protected zorgresultaat(result: NnnRegistrationResult): NocZorgresultaatSummary | undefined {
    return this.nocIndex().get(result.nnnResultDbRef);
  }

  protected zorgresultaatLabel(result: NnnRegistrationResult): string {
    const zorgresultaat = this.zorgresultaat(result);
    return zorgresultaat ? `${zorgresultaat.code} — ${zorgresultaat.omschrijving}` : result.nnnResultDbRef;
  }

  protected indicatorLabel(ref: string): string {
    return this.indicatorLabels().get(ref) ?? ref;
  }

  /** Compact "current → desired" score values for the panel header chip. */
  protected resultScoreSummary(
    result: NnnRegistrationResult
  ): { current: number | null; desired: number | null } | null {
    return this.scoreSummary(result.currentScoreNnnDbRef, result.desiredScoreNnnDbRef);
  }

  protected resultScoreTooltip(result: NnnRegistrationResult): string {
    return this.scoreTooltip(result.currentScoreNnnDbRef, result.desiredScoreNnnDbRef);
  }

  protected indicatorScoreSummary(
    indicator: NnnRegistrationResultIndicator
  ): { current: number | null; desired: number | null } | null {
    return this.scoreSummary(indicator.currentScoreNnnDbRef, indicator.desiredScoreNnnDbRef);
  }

  protected indicatorScoreTooltip(indicator: NnnRegistrationResultIndicator): string {
    return this.scoreTooltip(indicator.currentScoreNnnDbRef, indicator.desiredScoreNnnDbRef);
  }

  private scoreSummary(
    currentRef: string | null,
    desiredRef: string | null
  ): { current: number | null; desired: number | null } | null {
    const current = this.scoreNumber(currentRef);
    const desired = this.scoreNumber(desiredRef);
    return current === null && desired === null ? null : { current, desired };
  }

  private scoreTooltip(currentRef: string | null, desiredRef: string | null): string {
    const parts: string[] = [];
    if (currentRef) {
      parts.push(`${this.scoreFieldLabel('current')}: ${this.scoreLabel(currentRef)}`);
    }
    if (desiredRef) {
      parts.push(`${this.scoreFieldLabel('desired')}: ${this.scoreLabel(desiredRef)}`);
    }
    return parts.join('\n');
  }

  private scoreLabel(scoreRef: string): string {
    const score = this.scoreValues().get(scoreRef);
    return score ? `${score.meetwaardeSchaal} - ${score.omschrijving}` : scoreRef;
  }

  private scoreNumber(scoreRef: string | null): number | null {
    return scoreRef ? (this.scoreValues().get(scoreRef)?.meetwaardeSchaal ?? null) : null;
  }

  private scoreFieldLabel(field: 'current' | 'desired'): string {
    return this.languageService.translateWithCap(`epd.functions.careplan-nnn.scores.${field}`);
  }

  protected async openAdd(): Promise<void> {
    const data: EditNnnResultDialogData = {
      excludeRefs: this.results().map(r => r.nnnResultDbRef),
      diagnoseCode: this.diagnoseCode(),
    };
    const chosen = await this.openDialog(data);
    if (!chosen) {
      return;
    }
    const added: NnnRegistrationResult = {
      id: null,
      nnnResultDbRef: chosen.zorgresultaat.id,
      currentScoreNnnDbRef: chosen.currentScoreRef,
      desiredScoreNnnDbRef: chosen.desiredScoreRef,
      notes: null,
      indicators: chosen.indicatorRefs.map(ref => ({
        id: null,
        nnnIndicatorDbRef: ref,
        currentScoreNnnDbRef: chosen.indicatorScoreRefs[ref] ?? null,
        desiredScoreNnnDbRef: chosen.indicatorDesiredScoreRefs[ref] ?? null,
      })),
    };
    this.cacheLabels(chosen);
    this.resultsChange.emit([...this.results(), added]);
  }

  protected async openEdit(result: NnnRegistrationResult): Promise<void> {
    const zorgresultaat = this.zorgresultaat(result);
    if (!zorgresultaat) {
      return;
    }
    const data: EditNnnResultDialogData = {
      zorgresultaat,
      selectedIndicatorRefs: result.indicators.map(indicator => indicator.nnnIndicatorDbRef),
      currentScoreRef: result.currentScoreNnnDbRef,
      desiredScoreRef: result.desiredScoreNnnDbRef,
      indicatorScoreRefs: Object.fromEntries(
        result.indicators.map(indicator => [indicator.nnnIndicatorDbRef, indicator.currentScoreNnnDbRef])
      ),
      indicatorDesiredScoreRefs: Object.fromEntries(
        result.indicators.map(indicator => [indicator.nnnIndicatorDbRef, indicator.desiredScoreNnnDbRef])
      ),
    };
    const chosen = await this.openDialog(data);
    if (!chosen) {
      return;
    }
    const updated: NnnRegistrationResult = {
      ...result,
      currentScoreNnnDbRef: chosen.currentScoreRef,
      desiredScoreNnnDbRef: chosen.desiredScoreRef,
      indicators: reuseOrCreateByRef(
        chosen.indicatorRefs,
        result.indicators,
        indicator => indicator.nnnIndicatorDbRef,
        ref => ({ id: null, nnnIndicatorDbRef: ref, currentScoreNnnDbRef: null, desiredScoreNnnDbRef: null })
      ).map(indicator => ({
        ...indicator,
        currentScoreNnnDbRef: chosen.indicatorScoreRefs[indicator.nnnIndicatorDbRef] ?? null,
        desiredScoreNnnDbRef: chosen.indicatorDesiredScoreRefs[indicator.nnnIndicatorDbRef] ?? null,
      })),
    };
    this.cacheLabels(chosen);
    this.resultsChange.emit(this.results().map(r => (r === result ? updated : r)));
  }

  protected openInfo(): void {
    this.dialog.open(NnnResultsInfoDialogComponent, {
      width: '720px',
      panelClass: 'card-primary',
    });
  }

  protected remove(result: NnnRegistrationResult): void {
    this.resultsChange.emit(this.results().filter(r => r !== result));
  }

  protected onNotesInput(result: NnnRegistrationResult, value: string): void {
    result.notes = value;
  }

  protected saveNotes(): void {
    this.resultsChange.emit([...this.results()]);
  }

  private openDialog(data: EditNnnResultDialogData): Promise<EditNnnResultDialogResult | undefined> {
    const dialogRef = this.dialog.open(EditNnnResultDialogComponent, {
      width: data.zorgresultaat ? '50%' : '80%',
      panelClass: ['card-primary', 'nnn-edit-dialog-panel'],
      data,
    });
    return firstValueFrom(dialogRef.afterClosed());
  }

  private cacheLabels(chosen: EditNnnResultDialogResult): void {
    this.indicatorLabels.update(labels => {
      const next = new Map(labels);
      for (const indicator of chosen.indicators) {
        next.set(String(indicator.code), indicator.omschrijving);
      }
      return next;
    });
    this.fetchedZorgresultaten.add(chosen.zorgresultaat.code);
    this.cacheScoreValues(chosen.scoreValues);
  }
}
